import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Edit2, Archive, RefreshCw } from 'lucide-react';
import * as api from '../services/api';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

export default function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user', short_name: '', phone_mobile: '', phone_office: '', contact_email: '', job_title: '' });
  const [roles, setRoles] = useState([]);
  const [confirm, setConfirm] = useState({ isOpen: false });
  const toast = useToast();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([api.getUsers(), api.getRoles()]);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data.roles || []);
    } catch { toast.error(t('users.loadingError')); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, form);
        toast.success(t('common.saved'));
      } else {
        await api.createUser(form);
        toast.success(t('common.created'));
      }
      setShowModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, role: user.role, short_name: user.short_name || '', phone_mobile: user.phone_mobile || '', phone_office: user.phone_office || '', contact_email: user.contact_email || '', job_title: user.job_title || '', password: '' });
    setShowModal(true);
  };

  const handleArchive = (user) => {
    setConfirm({
      isOpen: true, title: t('users.archiveTitle'),
      message: t('users.archiveConfirm', { name: user.name }),
      onConfirm: async () => {
        try { await api.archiveUser(user.id); toast.success(t('users.archived')); loadData(); }
        catch { toast.error(t('common.error')); }
        finally { setConfirm({ isOpen: false }); }
      }
    });
  };

  const roleBadge = (role) => {
    const r = roles.find(r => r.role === role);
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${r?.color || '#6B7280'}20`, color: r?.color || '#6B7280' }}>
        {r?.display_name || role}
      </span>
    );
  };

  if (loading) return <div className="p-6 text-center text-gray-400">{t('common.loading')}</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-800">{t('users.title')}</h1>
        </div>
        <button onClick={() => { setEditingUser(null); setForm({ name: '', email: '', password: '', role: 'user', short_name: '', phone_mobile: '', phone_office: '', contact_email: '', job_title: '' }); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
          <Plus size={16} /> {t('users.addUser')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-gray-600">{t('users.nameLabel')}</th>
              <th className="text-left p-3 font-medium text-gray-600">{t('users.shortNameHeader')}</th>
              <th className="text-left p-3 font-medium text-gray-600">{t('common.email')}</th>
              <th className="text-left p-3 font-medium text-gray-600">{t('users.roleLabel')}</th>
              <th className="text-left p-3 font-medium text-gray-600">{t('common.status')}</th>
              <th className="text-right p-3 font-medium text-gray-600">{t('users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.filter(u => !u.is_archived).map(user => (
              <tr key={user.id} className="border-b hover:bg-gray-50">
                <td className="p-3 font-medium">{user.name}</td>
                <td className="p-3"><span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">{user.short_name || '—'}</span></td>
                <td className="p-3 text-gray-500">{user.email}</td>
                <td className="p-3">{roleBadge(user.role)}</td>
                <td className="p-3">
                  <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-red-500'}`}>
                    {user.is_active ? t('common.active') : t('common.disabled')}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => handleEdit(user)} className="p-1.5 hover:bg-gray-100 rounded" title={t('common.edit')}>
                    <Edit2 size={14} className="text-gray-400" />
                  </button>
                  <button onClick={() => handleArchive(user)} className="p-1.5 hover:bg-gray-100 rounded" title={t('users.archiveTitle')}>
                    <Archive size={14} className="text-gray-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingUser ? t('users.editUser') : t('users.newUser')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.nameLabel')}</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.shortNameLabel')}</label>
            <input value={form.short_name} onChange={e => setForm({ ...form, short_name: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10) })}
              className="w-full px-3 py-2 border rounded-lg font-mono" placeholder={t('users.shortNamePlaceholder')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.jobTitleLabel')}</label>
            <input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" placeholder={t('users.jobTitlePlaceholder')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.phoneMobileLabel')}</label>
              <input value={form.phone_mobile} onChange={e => setForm({ ...form, phone_mobile: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" placeholder="+48 600 000 000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.phoneOfficeLabel')}</label>
              <input value={form.phone_office} onChange={e => setForm({ ...form, phone_office: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" placeholder="+48 62 000 00 00" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.contactEmailLabel')}</label>
            <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" placeholder={t('users.contactEmailPlaceholder')} />
          </div>
          {!editingUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.passwordLabel')}</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" required={!editingUser} />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.roleLabel')}</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg">
              {roles.filter(r => r.role !== 'pending').map(r => (
                <option key={r.role} value={r.role}>{r.display_name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('common.save')}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={confirm.isOpen} title={confirm.title} message={confirm.message}
        onConfirm={confirm.onConfirm} onCancel={() => setConfirm({ isOpen: false })} type="warning" />
    </div>
  );
}
